import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faComment, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { onSocketEvent, offSocketEvent } from "../../services/socket";
import "./inbox.css";

// Humanized labels for the ride-status pill. `upcoming` is the default active
// state, so it gets no pill — only states worth flagging show one.
const STATUS_LABELS = {
  past_due: "Past due",
  in_transit: "In transit",
  completed: "Completed",
  fulfilled: "Fulfilled",
  expired: "Expired",
};

function relativeTime(dateStr) {
  const then = new Date(dateStr).getTime();
  const diff = Date.now() - then;
  const min = Math.floor(diff / 60000);
  if (min < 1) return "now";
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  return new Date(dateStr).toLocaleDateString([], { month: "short", day: "numeric" });
}

function formatRideDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

const Inbox = ({ isLoggedIn, loginUser, onLogout }) => {
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchThreads = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/v1/messages");
      setThreads(data.threads || []);
    } catch (err) {
      if (err.response && err.response.status === 401) {
        navigate("/login");
        return;
      }
      // Silent otherwise — leave whatever we have
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  // Keep the list fresh: a new chat message arrives as a personal notification.
  useEffect(() => {
    const handleNotification = (notification) => {
      if (notification.type === "new_message") fetchThreads();
    };
    onSocketEvent("notification", handleNotification);
    return () => offSocketEvent("notification", handleNotification);
  }, [fetchThreads]);

  if (!isLoggedIn) {
    navigate("/");
    return null;
  }

  const openThread = (rideId) => navigate(`/ride/${rideId}?chat=1`);

  return (
    <div className="sr-inbox">
      <div className="sr-inbox__header">
        <h1>Messages</h1>
        <p>Your active ride chats</p>
      </div>

      {loading ? (
        <div className="sr-inbox__list">
          {[0, 1, 2].map((i) => (
            <div key={i} className="sr-inbox__row sr-inbox__row--skeleton" />
          ))}
        </div>
      ) : threads.length === 0 ? (
        <div className="sr-inbox__empty">
          <span className="sr-inbox__empty-icon">
            <FontAwesomeIcon icon={faComment} />
          </span>
          <h3>No conversations yet</h3>
          <p>When you offer or book a ride, your chat with the other side shows up here.</p>
        </div>
      ) : (
        <div className="sr-inbox__list">
          {threads.map((t) => {
            const isInquiry = t.kind === "inquiry";
            const statusLabel = STATUS_LABELS[t.status];
            const preview =
              t.lastMessage.senderId === loginUser._id
                ? `You: ${t.lastMessage.content}`
                : `${t.lastMessage.senderName}: ${t.lastMessage.content}`;
            const key = isInquiry ? `inq-${t.inquiryId}` : `ride-${t.rideId}`;
            const onOpen = () =>
              isInquiry ? navigate(`/inbox/inquiry/${t.inquiryId}`) : openThread(t.rideId);
            return (
              <button
                key={key}
                className={`sr-inbox__row${t.unread > 0 ? " sr-inbox__row--unread" : ""}`}
                onClick={onOpen}
              >
                <div className="sr-inbox__row-main">
                  <div className="sr-inbox__route">
                    <span className="sr-inbox__route-city">{t.departure}</span>
                    <FontAwesomeIcon icon={faArrowRight} className="sr-inbox__route-arrow" />
                    <span className="sr-inbox__route-city">{t.arrival}</span>
                    {isInquiry && <span className="sr-inbox__tag">Inquiry</span>}
                    {statusLabel && (
                      <span className={`sr-inbox__status sr-inbox__status--${t.status}`}>
                        {statusLabel}
                      </span>
                    )}
                  </div>
                  <div className="sr-inbox__preview">
                    {isInquiry ? `${t.otherPartyName} · ${preview}` : preview}
                  </div>
                  {t.estimatedDate && (
                    <div className="sr-inbox__date">{formatRideDate(t.estimatedDate)}</div>
                  )}
                </div>
                <div className="sr-inbox__row-meta">
                  <span className="sr-inbox__time">{relativeTime(t.lastMessage.createdAt)}</span>
                  {t.unread > 0 && (
                    <span className="sr-inbox__unread-badge">
                      {t.unread > 99 ? "99+" : t.unread}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Inbox;
